/**
 * Channel operations (spec §9.2 — B20b(g) ruling 2026-07-14).
 *
 * "Embedding is channel configuration, not new commerce machinery": one
 * write op sets the publishable site key, the exact-origin allowlist that
 * feeds BOTH the API origin fence and the frame-ancestors CSP (B31a's
 * embedding module owns the cross-field validation), and optionally the
 * payment provider's publishable key (§7 step 4). The contract carries the
 * data; the domain walls the shape — the promotion.create precedent.
 */

import { z } from "zod";
import { publicTenantlessOp, readOp, writeOp, zId } from "./common.js";

/**
 * B52 — the theme color-role vocabulary (drift-walled against the domain's
 * CHANNEL_THEME_ROLES by test; contracts stays zod-only so the list is
 * duplicated by necessity). Values are the --transact-* token roles.
 */
export const CHANNEL_THEME_COLOR_ROLES = [
  "accent",
  "accentStrong",
  "accentContrast",
  "accentSoft",
  "accentText",
  "ink",
  "onInk",
  "bg",
  "surface",
  "text",
  "muted",
  "border",
] as const;

const zHexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "6-digit hex (#rrggbb)");

/** B52: brand theme — logo + color overrides; contrast walls live in the domain. Exported for the ops that carry it (channel.config.set, session.guest). */
export const zChannelTheme = z
  .object({
    logoUrl: z.string().min(1).optional(),
    /**
     * B403 #26 (a) — the hand-written storefront headline (75:2's
     * "Sailings, day passes and bikes"): the B397-priced channel
     * amendment landing. Display-only; the storefront falls back to its
     * catalog-derived summary when unset.
     */
    headline: z.string().min(1).max(120).optional(),
    colors: z
      .object(Object.fromEntries(CHANNEL_THEME_COLOR_ROLES.map((role) => [role, zHexColor.optional()])))
      .strict()
      .optional(),
  })
  .strict();

export const channelConfigSet = writeOp({
  name: "channel.config.set",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    channelId: zId,
    config: z
      .object({
        /** Publishable (non-secret) site key identifying the channel in embeds. */
        siteKey: z.string().min(1),
        /** Exact origins; wildcards and paths are refused by the domain wall. */
        allowedOrigins: z.array(z.string().min(1)).min(1),
        /** Provider PUBLISHABLE key (e.g. Stripe pk_…) — secret-shaped keys are refused. */
        providerPublishableKey: z.string().min(1).optional(),
        /** B52: brand theme (logo + color overrides), contrast-walled in the domain. */
        theme: zChannelTheme.optional(),
        /**
         * B549 — the group-booking threshold: a cart reaching this many
         * units on this channel is the groups team's, not self-service.
         * Absent = the platform default (20); 0 = OFF (the groups desk's
         * own channel sells any size).
         */
        groupThreshold: z.number().int().min(0).max(100_000).optional(),
        /**
         * B549 (9) — the first name the groups desk signs with on the buyer
         * surfaces ("Maya sends a quote"). Absent = the desk speaks as the desk.
         */
        groupsContactName: z.string().min(1).max(40).optional(),
        /**
         * B545 (2), RULED 2026-09-24 — on for the web storefront's channels,
         * off for kiosk (absent = off): payment.intent.create refuses an
         * intent for an order with no buyer email where this is on.
         */
        requireBuyerEmail: z.boolean().optional(),
        /** B609: a reseller sale draws its carve-out first; absent = on, false = the open pool only. */
        carveOutsDrawFirst: z.boolean().optional(),
      })
      .strict(),
  },
  /**
   * Echoes the NORMALIZED origin list — what both walls will actually check.
   *
   * B221 — and the publishable key, which this echo did not declare for as
   * long as it existed. The comment below has always called this response
   * "the stored truth", and the channels screen believes it: it refills the
   * whole draft from this body so an untouched save re-sends the record
   * verbatim. Since B168 the client parses through `tolerantResponse`, which
   * STRIPS any undeclared key, so the field could never survive whatever the
   * handler emitted — the draft refilled with a hole and the next
   * whole-config upsert dropped the key. Configure a pk, save twice, the pk
   * is gone. Growing the echo (rather than teaching the screen to distrust
   * it) is the fix that makes the comment true; `channel.config.get` has
   * always echoed the same field, and echoing it leaks nothing because the
   * write's own wall refuses secret-shaped keys.
   */
  response: z
    .object({
      channelId: zId,
      siteKey: z.string().min(1),
      allowedOrigins: z.array(z.string().min(1)).min(1),
      /** B221: present exactly when one is stored — absent, never null. */
      providerPublishableKey: z.string().min(1).optional(),
      /** B52: echoes the STORED theme when one is configured. */
      theme: zChannelTheme.optional(),
      /** B549: present exactly when one is stored — absent means the default (20). */
      groupThreshold: z.number().int().min(0).optional(),
      /** B549 (9): present exactly when one is stored. */
      groupsContactName: z.string().min(1).max(40).optional(),
      /** B545 (2): present exactly when one is stored — absent means off. */
      requireBuyerEmail: z.boolean().optional(),
      /** B609: a reseller sale draws its carve-out first; absent = on, false = the open pool only. */
      carveOutsDrawFirst: z.boolean().optional(),
    })
    .strict(),
  exampleRequest: {
    channelId: "web",
    config: { siteKey: "sk-webchannel01", allowedOrigins: ["https://tickets.example.com"], providerPublishableKey: "pk_test_example" },
  },
  exampleResponse: {
    channelId: "web",
    siteKey: "sk-webchannel01",
    allowedOrigins: ["https://tickets.example.com"],
    providerPublishableKey: "pk_test_example",
  },
});

/**
 * B91 (GAP-3) — the read the onboarding/channels screen stands on: what
 * channel.config.set stored, echoed back verbatim (origins NORMALIZED — the
 * list both enforcement walls actually check). providerPublishableKey is
 * publishable BY THE WRITE'S OWN WALL (secret-shaped keys never stored), so
 * echoing it leaks nothing. Absent channel ≡ foreign tenant: one 404 (Inv 10).
 * Permission rides the config scope both ways — a dedicated channel:read
 * would be an authorizer amendment (deliberately not taken; B80 precedent).
 */
export const channelConfigGet = readOp({
  name: "channel.config.get",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    channelId: zId,
  },
  response: z
    .object({
      channelId: zId,
      siteKey: z.string().min(1),
      allowedOrigins: z.array(z.string().min(1)).min(1),
      /** Present only when the write stored one; never null. */
      providerPublishableKey: z.string().min(1).optional(),
      theme: zChannelTheme.optional(),
      groupThreshold: z.number().int().min(0).optional(),
      groupsContactName: z.string().min(1).max(40).optional(),
      requireBuyerEmail: z.boolean().optional(),
      /** B609: a reseller sale draws its carve-out first; absent = on, false = the open pool only. */
      carveOutsDrawFirst: z.boolean().optional(),
    })
    .strict(),
  exampleRequest: { channelId: "web" },
  exampleResponse: { channelId: "web", siteKey: "sk-webchannel01", allowedOrigins: ["https://tickets.example.com"], providerPublishableKey: "pk_test_example" },
});

/**
 * B645 (b) — "Pick from your logo" (RULED 2026-10-05, Pathik: "B645b yes" —
 * the 2026-10-03 recommendation verbatim): ADMIN only; reads ONLY the
 * channel's SAVED https logo address (the request names the channel, never a
 * URL); the demo builder's fetch fence (public addresses, every redirect hop
 * re-checked, size + time caps); PNG and SVG; at most six colours; stores
 * nothing. Same seat + scope as channel.config.get (the B80 precedent).
 */
export const channelLogoColors = readOp({
  name: "channel.logo.colors",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: {
    channelId: zId,
  },
  response: z
    .object({
      /** Most-used first, CAPS hex (the swatch line's shape); empty = none could be read. */
      colors: z.array(zHexColor).max(6),
    })
    .strict(),
  exampleRequest: { channelId: "web" },
  exampleResponse: { colors: ["#C8102E", "#1C2A4A", "#FFFFFF"] },
});

/**
 * B92(3) — channel.config.list: every channel the tenant sells through,
 * the same rows .get answers, sorted by channelId. Zero channels is an
 * honest empty list (the screen's teaching state), never an error. Same
 * seat and scope as .get (the B80 precedent — no authorizer amendment);
 * tenant-fenced by construction: the store is keyed by tenantId.
 */
export const channelConfigList = readOp({
  name: "channel.config.list",
  permission: "channel:write",
  principals: ["STAFF:ADMIN"],
  fields: {},
  response: z
    .object({
      channels: z.array(
        z
          .object({
            channelId: zId,
            siteKey: z.string().min(1),
            allowedOrigins: z.array(z.string().min(1)).min(1),
            providerPublishableKey: z.string().min(1).optional(),
            theme: zChannelTheme.optional(),
            groupThreshold: z.number().int().min(0).optional(),
            groupsContactName: z.string().min(1).max(40).optional(),
            requireBuyerEmail: z.boolean().optional(),
            /** B609: a reseller sale draws its carve-out first; absent = on, false = the open pool only. */
            carveOutsDrawFirst: z.boolean().optional(),
          })
          .strict(),
      ),
    })
    .strict(),
  exampleRequest: {},
  exampleResponse: { channels: [{ channelId: "web", siteKey: "sk-webchannel01", allowedOrigins: ["https://tickets.example.com"] }] },
});

/**
 * B181 (RULED via Slack, Pathik 2026-09-21) — the tenantless PUBLIC
 * resolve: the hosted storefront's first breath. A siteKey is
 * publishable-class BY DESIGN (it ships in embed snippets and operator
 * URLs), so resolving it answers only what a public booking page shows
 * anyway: which tenant and channel to boot. Everything downstream keeps
 * its own walls — the guest mint keeps B103's source fence and B31a's
 * origin binding; this door cannot mint, read or sell anything.
 *
 * Oracle-free by construction: unknown, malformed and revoked keys all
 * answer the SAME empty shape (a revoked key's claim is released, so it
 * is literally indistinguishable from never-existed). The handler's
 * per-source rate wall fails closed when unwired (the B413 stance — a
 * tenantless door has no other wall).
 */
export const channelResolve = publicTenantlessOp({
  name: "channel.resolve",
  permission: "public",
  fields: { siteKey: z.string().min(1).max(128) },
  response: z
    .object({ channel: z.object({ tenantId: zId, channelId: zId }).strict().optional() })
    .strict(),
  exampleRequest: { siteKey: "sk-webchannel01" },
  exampleResponse: { channel: { tenantId: "tenant-1", channelId: "web" } },
});
